"use client"

import * as React from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { toastError, presentMutationError } from "../utils/showApiErrorToast";
import {
  Check,
  ChevronRight,
  ChevronsUpDown,
  Loader2,
  Plus,
} from "lucide-react"

import { BrokerMark } from "@/components/Broker/BrokerMark"
import { SIDEBAR_BRAND_LOGO_COLLAPSED_SRC } from "@/constants/sidebarBrandLogo"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useActiveBrokerAccount } from "@/hooks/useActiveBrokerAccount"
import { useSidebarExpandedMenuChrome } from "@/hooks/useSidebarExpandedMenuChrome"
import type { TradeAccountOption } from "@/hooks/useActiveBrokerAccount"
import { fetchBrokerCapabilities } from "@/services/brokerIntegrationApi"
import {
  formatAccountDisplayLabel,
  formatNestedBrokerAccountLabel,
  formatTradeAccountPrimaryLabel,
} from "@/services/liquidBrokersApi"
import { normalizeBrokerSlug } from "@/utils/brokerLiveSync"
import { filterConnectedTradeAccountsForUi } from "@/utils/tradeAccountSource"
import { resolveBrokerDisplayName } from "@/utils/customCsvAccountDisplay"
import {
  sidebarGray500Class,
  sidebarMenuButtonIconRailClass,
  notificationsCentreIconRailButtonClass,
  sidebarSecondaryTextClass,
  sidebarTropicalSurfClass,
} from "@/constants/sidebarChrome"
import { cn } from "@/lib/utils"

const switcherMenuButtonClass = cn(
  "gap-2.5 !pl-2 !pr-2 !py-2",
  sidebarMenuButtonIconRailClass,
  "data-[state=open]:bg-content-canvas",
  "data-[state=open]:hover:bg-content-canvas",
  "data-[state=open]:active:bg-content-canvas"
)

function switcherTriggerClassName(forceIconRail: boolean) {
  return cn(switcherMenuButtonClass, forceIconRail && notificationsCentreIconRailButtonClass)
}

/** 1px stroke matches Statistics distribution chart cards (`statisticsShadcnCardClassName`). */
const brokerMarkFrameStrokeClass =
  "rounded-[6px] border border-solid border-border p-0.5 !shadow-none ring-0"

const brokerMarkFrameClass = cn(brokerMarkFrameStrokeClass, "bg-background")

const PROFILE_BROKER_URL = "/profile?tab=broker-api"
/** Horizontal gap between the brokers menu and accounts panel. */
const BROKER_ACCOUNTS_PANEL_GAP = 8

/**
 * Outer: space between panel edge and row tracks (profile menu `px-2 py-2`).
 * Inner: space inside each row highlight (`SidebarMenuButton` / account tiles).
 */
const brokerAccountPanelOuterPadding = "px-2 py-2"
/** Inset inside row hover chrome (wrapper avoids Chakra/native button padding resets). */
const brokerAccountPanelInnerPadding = "px-2"

const brokerAccountPanelRowInnerClass =
  "flex h-full w-full min-w-0 items-center gap-2 px-2"

const brokerAccountPanelLabelClass = cn(
  "flex h-7 shrink-0 items-center text-xs font-medium text-muted-foreground",
  brokerAccountPanelInnerPadding
)

/** Row height shared by broker + account lists so rows align across both panels. */
const ACCOUNT_ROW_HEIGHT_PX = 52
const ACCOUNTS_VISIBLE_COUNT = 5
const accountsListMaxHeight = ACCOUNT_ROW_HEIGHT_PX * ACCOUNTS_VISIBLE_COUNT

const brokerAccountPanelRowBaseClass =
  "flex w-full shrink-0 cursor-default items-center rounded-[6px] text-sm outline-hidden select-none transition-[color,background-color,box-shadow] duration-150 ease-linear"

const brokerAccountPanelRowClass = cn(brokerAccountPanelRowBaseClass, "h-[52px]")

const brokerAccountPanelShellClass = cn(
  "flex flex-col overflow-hidden rounded-lg bg-popover text-popover-foreground shadow-lg ring-1 ring-foreground/10",
  brokerAccountPanelOuterPadding
)

const brokerAccountPanelMobileShellClass = cn(
  "flex w-full min-w-0 flex-col overflow-hidden rounded-[6px] border border-solid border-border bg-content-canvas text-sidebar-muted-foreground shadow-none dark:text-white",
  brokerAccountPanelOuterPadding
)

const accountRowClass = cn(brokerAccountPanelRowClass, "text-left")

const brokerAccordionTriggerClass = cn(
  brokerAccountPanelRowClass,
  "items-center gap-0 rounded-[6px] border-0 py-0 font-normal hover:no-underline",
  "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground",
  "focus-visible:border-transparent focus-visible:ring-0 focus-visible:after:border-transparent"
)

function MobileBrokerPanelScroll({
  children,
}: {
  children: React.ReactNode
}) {
  return (
    <div
      data-broker-panel-mobile-scroll=""
      className="min-h-0 max-h-[min(70vh,28rem)] touch-pan-y overflow-x-hidden overflow-y-auto overscroll-contain [-ms-overflow-style:none] [scrollbar-width:none] [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden"
      onTouchMove={(event) => event.stopPropagation()}
      onWheel={(event) => event.stopPropagation()}
    >
      {children}
    </div>
  )
}

function AccountSelectRow({
  account,
  brokerGroupLabel,
  active,
  disabled,
  onSelect,
}: {
  account: TradeAccountOption
  brokerGroupLabel: string
  active: boolean
  disabled: boolean
  onSelect: (brokerName: string, accountCode: string) => void
}) {
  return (
    <button
      type="button"
      role="menuitem"
      data-sidebar="menu-button"
      data-active={active ? "true" : undefined}
      disabled={disabled}
      className={cn(accountRowClass, "text-left outline-hidden")}
      onClick={() => onSelect(account.brokerName, account.accountCode)}
    >
      <div data-broker-row-inner className={brokerAccountPanelRowInnerClass}>
        <span className="min-w-0 flex-1 truncate font-medium">
          {nestedAccountRowLabel(account, brokerGroupLabel)}
        </span>
        {active ? <Check className="ml-auto size-4 shrink-0" /> : null}
      </div>
    </button>
  )
}

function AccountsListScroll({
  accountCount,
  children,
}: {
  accountCount: number
  children: React.ReactNode
}) {
  const scrollable = accountCount > ACCOUNTS_VISIBLE_COUNT
  const height = Math.min(accountCount, ACCOUNTS_VISIBLE_COUNT) * ACCOUNT_ROW_HEIGHT_PX
  return (
    <div
      className={cn(
        "min-h-0 touch-pan-y",
        scrollable
          ? "overflow-x-hidden overflow-y-scroll overscroll-contain"
          : "overflow-visible"
      )}
      style={{
        height,
        maxHeight: accountsListMaxHeight,
        WebkitOverflowScrolling: scrollable ? "touch" : undefined,
      }}
      onWheel={(event) => event.stopPropagation()}
    >
      {children}
    </div>
  )
}

type BrokerGroup = {
  id: string
  name: string
  accounts: TradeAccountOption[]
}

function groupAccountsByBroker(
  accounts: TradeAccountOption[],
  resolveBrokerLabel: (brokerSlug: string, fallback: string) => string
): BrokerGroup[] {
  const map = new Map<string, BrokerGroup>()
  for (const account of accounts) {
    const id = account.brokerName
    const existing = map.get(id)
    if (existing) {
      existing.accounts.push(account)
    } else {
      const fallback = account.brokerDisplayName || account.brokerName
      map.set(id, {
        id,
        name: resolveBrokerLabel(id, fallback),
        accounts: [account],
      })
    }
  }
  return Array.from(map.values()).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  )
}

function accountRowLabel(account: TradeAccountOption): string {
  return formatTradeAccountPrimaryLabel(account)
}

function nestedAccountRowLabel(
  account: TradeAccountOption,
  brokerGroupLabel: string
): string {
  return formatNestedBrokerAccountLabel(account, brokerGroupLabel)
}

function isActiveAccount(
  account: TradeAccountOption,
  activeBrokerName: string | null,
  activeAccountCode: string | null
): boolean {
  return (
    account.brokerName === activeBrokerName &&
    account.accountCode === activeAccountCode
  )
}

/** Active account first so users do not scroll to find the current selection. */
function sortAccountsActiveFirst(
  accounts: TradeAccountOption[],
  activeBrokerName: string | null,
  activeAccountCode: string | null
): TradeAccountOption[] {
  if (!activeBrokerName || !activeAccountCode || accounts.length <= 1) {
    return accounts
  }
  const activeIndex = accounts.findIndex((account) =>
    isActiveAccount(account, activeBrokerName, activeAccountCode)
  )
  if (activeIndex <= 0) {
    return accounts
  }
  const sorted = [...accounts]
  const [active] = sorted.splice(activeIndex, 1)
  return [active, ...sorted]
}

function TesseraLogoMark({ className }: { className?: string }) {
  return (
    <div
      data-broker-mark-frame
      className={cn(
        "flex aspect-square shrink-0 items-center justify-center",
        brokerMarkFrameClass,
        className
      )}
    >
      <img
        src={SIDEBAR_BRAND_LOGO_COLLAPSED_SRC}
        alt="Tessera"
        className="size-full max-h-full max-w-full object-contain"
        decoding="async"
      />
    </div>
  )
}

export function BrokerAccountSwitcher({
  forceIconRail = false,
}: {
  forceIconRail?: boolean
} = {}) {
  const { isMobile } = useSidebar()
  const showMenuChrome = useSidebarExpandedMenuChrome() && !forceIconRail
  const {
    accounts,
    activeAccountCode,
    activeBrokerName,
    loading,
    isConnected,
    setActiveTradeAccount,
  } = useActiveBrokerAccount()

  const connectedAccounts = React.useMemo(
    () => filterConnectedTradeAccountsForUi(accounts),
    [accounts]
  )

  const [isSwitching, setIsSwitching] = React.useState(false)
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [brokerDisplayNamesBySlug, setBrokerDisplayNamesBySlug] = React.useState<
    Record<string, string>
  >({})

  React.useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const caps = await fetchBrokerCapabilities()
        if (cancelled || !Array.isArray(caps)) return
        const next: Record<string, string> = {}
        caps.forEach((cap) => {
          const slug = normalizeBrokerSlug(cap.broker_name)
          next[slug] = resolveBrokerDisplayName(cap)
        })
        setBrokerDisplayNamesBySlug(next)
      } catch {
        if (!cancelled) setBrokerDisplayNamesBySlug({})
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const resolveBrokerLabel = React.useCallback(
    (brokerSlug: string, fallback: string) => {
      const slug = normalizeBrokerSlug(brokerSlug)
      return brokerDisplayNamesBySlug[slug] ?? fallback
    },
    [brokerDisplayNamesBySlug]
  )
  /** Shown while hovering a broker row (desktop); cleared on mouse leave unless pinned. */
  const [hoveredBrokerId, setHoveredBrokerId] = React.useState<string | null>(
    null
  )
  /** Set when user clicks a broker; accounts panel stays open until the menu closes. */
  const [pinnedBrokerId, setPinnedBrokerId] = React.useState<string | null>(
    null
  )
  const [mobileExpandedBrokerId, setMobileExpandedBrokerId] = React.useState("")
  const displayBrokerId = hoveredBrokerId ?? pinnedBrokerId
  const brokerGroups = React.useMemo(
    () => groupAccountsByBroker(connectedAccounts, resolveBrokerLabel),
    [connectedAccounts, resolveBrokerLabel]
  )

  const activeAccount = React.useMemo(
    () =>
      accounts.find(
        (a) =>
          a.brokerName === activeBrokerName &&
          a.accountCode === activeAccountCode
      ) ?? null,
    [accounts, activeBrokerName, activeAccountCode]
  )

  const displayBroker = React.useMemo(
    () => brokerGroups.find((b) => b.id === displayBrokerId) ?? null,
    [brokerGroups, displayBrokerId]
  )

  const accountsForDisplayBroker = React.useMemo(
    () =>
      displayBroker
        ? sortAccountsActiveFirst(
            displayBroker.accounts,
            activeBrokerName,
            activeAccountCode
          )
        : [],
    [displayBroker, activeBrokerName, activeAccountCode]
  )

  const mobileAccordionDefaultBrokerId = React.useMemo(() => {
    if (
      activeBrokerName &&
      brokerGroups.some((broker) => broker.id === activeBrokerName)
    ) {
      return activeBrokerName
    }
    return brokerGroups[0]?.id ?? ""
  }, [activeBrokerName, brokerGroups])

  React.useEffect(() => {
    if (menuOpen && isMobile) {
      setMobileExpandedBrokerId(mobileAccordionDefaultBrokerId)
    }
  }, [menuOpen, isMobile, mobileAccordionDefaultBrokerId])

  const switcherCloseTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const switcherContentRef = React.useRef<HTMLDivElement | null>(null)
  const switcherTriggerRef = React.useRef<HTMLButtonElement | null>(null)
  const [mobileSidebarContainer, setMobileSidebarContainer] =
    React.useState<HTMLElement | null>(null)

  React.useLayoutEffect(() => {
    if (!isMobile) {
      setMobileSidebarContainer(null)
      return
    }
    setMobileSidebarContainer(
      document.querySelector<HTMLElement>('[data-slot="sidebar"][data-mobile="true"]')
    )
  }, [isMobile, menuOpen])

  const cancelSwitcherClose = React.useCallback(() => {
    if (switcherCloseTimerRef.current != null) {
      clearTimeout(switcherCloseTimerRef.current)
      switcherCloseTimerRef.current = null
    }
  }, [])

  React.useEffect(() => () => cancelSwitcherClose(), [cancelSwitcherClose])

  const closeSwitcherUnlessPinned = React.useCallback(() => {
    cancelSwitcherClose()
    setHoveredBrokerId(null)
    if (!pinnedBrokerId) {
      setMenuOpen(false)
    }
  }, [cancelSwitcherClose, pinnedBrokerId])

  const scheduleSwitcherClose = React.useCallback(() => {
    if (isMobile || pinnedBrokerId || !menuOpen) return
    cancelSwitcherClose()
    switcherCloseTimerRef.current = setTimeout(() => {
      switcherCloseTimerRef.current = null
      closeSwitcherUnlessPinned()
    }, 120)
  }, [
    isMobile,
    pinnedBrokerId,
    menuOpen,
    cancelSwitcherClose,
    closeSwitcherUnlessPinned,
  ])

  const handleMenuOpenChange = React.useCallback(
    (open: boolean) => {
      setMenuOpen(open)
      if (!open) {
        cancelSwitcherClose()
        setHoveredBrokerId(null)
        setPinnedBrokerId(null)
        setMobileExpandedBrokerId("")
      }
    },
    [cancelSwitcherClose]
  )

  const pinBroker = React.useCallback((brokerId: string) => {
    cancelSwitcherClose()
    setPinnedBrokerId(brokerId)
    setHoveredBrokerId(brokerId)
  }, [cancelSwitcherClose])

  const handleBrokerRowMouseEnter = React.useCallback(
    (brokerId: string) => {
      if (isMobile) return
      cancelSwitcherClose()
      setHoveredBrokerId(brokerId)
    },
    [isMobile, cancelSwitcherClose]
  )

  const handleBrokerPanelMouseEnter = React.useCallback(() => {
    if (isMobile) return
    cancelSwitcherClose()
    if (!pinnedBrokerId && activeBrokerName) {
      setHoveredBrokerId((prev) => prev ?? activeBrokerName)
    }
  }, [isMobile, pinnedBrokerId, activeBrokerName, cancelSwitcherClose])

  const handleSwitcherPanelsMouseEnter = React.useCallback(() => {
    cancelSwitcherClose()
  }, [cancelSwitcherClose])

  const handleSwitcherPanelsMouseLeave = React.useCallback(() => {
    if (isMobile) return
    closeSwitcherUnlessPinned()
  }, [isMobile, closeSwitcherUnlessPinned])

  const handleTriggerPointerLeave = React.useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (isMobile || pinnedBrokerId || !menuOpen) return
      const related = event.relatedTarget
      if (
        related instanceof Node &&
        switcherContentRef.current?.contains(related)
      ) {
        return
      }
      scheduleSwitcherClose()
    },
    [isMobile, pinnedBrokerId, menuOpen, scheduleSwitcherClose]
  )

  const handleSelectAccount = React.useCallback(
    async (brokerName: string, accountCode: string) => {
      if (
        isSwitching ||
        (brokerName === activeBrokerName && accountCode === activeAccountCode)
      ) {
        setMenuOpen(false)
        return
      }
      setIsSwitching(true)
      try {
        await setActiveTradeAccount(brokerName, accountCode)
        const label = accounts.find(
          (a) =>
            a.accountCode === accountCode && a.brokerName === brokerName
        )
        const displayName = label
          ? formatAccountDisplayLabel(label)
          : accountCode
        toast.success("Switched account", {
          description: `Now viewing ${displayName}`,
        })
        setMenuOpen(false)
      } catch (e) {
        presentMutationError(e, "Failed to switch account")
      } finally {
        setIsSwitching(false)
      }
    },
    [
      accounts,
      activeAccountCode,
      activeBrokerName,
      isSwitching,
      setActiveTradeAccount,
    ]
  )

  const triggerPrimary = loading
    ? "Loading…"
    : activeAccount
      ? accountRowLabel(activeAccount)
      : isConnected
        ? "Select account"
        : "Connect broker"

  const activeBrokerLabel = activeAccount
    ? resolveBrokerLabel(
        activeAccount.brokerName,
        activeAccount.brokerDisplayName || activeAccount.brokerName
      )
    : ""

  const triggerSecondary = loading
    ? "Trade account"
    : activeAccount
      ? activeBrokerLabel
      : isConnected
        ? "No account selected"
        : "Add a broker in Profile"

  const triggerTooltip = activeAccount
    ? `${accountRowLabel(activeAccount)} — ${activeBrokerLabel}`
    : "Broker & account"

  if (!loading && connectedAccounts.length === 0) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton
            size={forceIconRail ? "default" : "lg"}
            tooltip={triggerTooltip}
            className={cn(
              switcherTriggerClassName(forceIconRail),
              showMenuChrome && "overflow-visible"
            )}
            asChild
          >
            <Link to={PROFILE_BROKER_URL}>
              <TesseraLogoMark className="size-8" />
              {showMenuChrome ? (
                <>
                  <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                    <span className={cn("truncate font-medium", sidebarTropicalSurfClass)}>
                      Connect broker
                    </span>
                    <span className={cn("truncate text-xs", sidebarSecondaryTextClass)}>
                      Profile → Broker Management
                    </span>
                  </div>
                  <Plus className={cn("ml-auto size-4 shrink-0", sidebarGray500Class)} />
                </>
              ) : null}
            </Link>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  return (
    <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu
          open={menuOpen}
          onOpenChange={handleMenuOpenChange}
          modal={isMobile}
        >
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              ref={switcherTriggerRef}
              data-broker-account-switcher-trigger=""
              size={forceIconRail ? "default" : "lg"}
              tooltip={showMenuChrome ? undefined : triggerTooltip}
              className={cn(
                switcherTriggerClassName(forceIconRail),
                showMenuChrome && "overflow-visible"
              )}
              disabled={loading || isSwitching}
              onPointerLeave={handleTriggerPointerLeave}
            >
              {loading || isSwitching ? (
                <div
                  data-broker-mark-frame
                  className={cn(
                    "flex size-8 shrink-0 items-center justify-center bg-sidebar-accent",
                    brokerMarkFrameClass
                  )}
                >
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <TesseraLogoMark className="size-8" />
              )}
              {showMenuChrome ? (
                <>
                  <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                    <span className={cn("truncate font-medium", sidebarTropicalSurfClass)}>
                      {triggerPrimary}
                    </span>
                    <span className={cn("truncate text-xs", sidebarSecondaryTextClass)}>
                      {isSwitching ? "Switching…" : triggerSecondary}
                    </span>
                  </div>
                  <ChevronsUpDown
                    className={cn("ml-auto size-4 shrink-0", sidebarGray500Class)}
                    aria-hidden
                  />
                </>
              ) : null}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            ref={switcherContentRef}
            data-broker-account-switcher-menu=""
            data-broker-account-switcher-mobile={isMobile ? "" : undefined}
            portalContainer={isMobile ? mobileSidebarContainer : undefined}
            collisionBoundary={
              isMobile && mobileSidebarContainer ? mobileSidebarContainer : undefined
            }
            className={cn(
              isMobile
                ? "w-[var(--radix-dropdown-menu-trigger-width)] min-w-[var(--radix-dropdown-menu-trigger-width)] max-w-[var(--radix-dropdown-menu-trigger-width)] overflow-visible rounded-none border-0 bg-transparent p-0 shadow-none ring-0"
                : "flex w-auto items-start gap-2 overflow-visible rounded-lg border-0 bg-transparent p-0 shadow-none ring-0"
            )}
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={BROKER_ACCOUNTS_PANEL_GAP}
            avoidCollisions={isMobile}
            collisionPadding={8}
            onMouseEnter={handleSwitcherPanelsMouseEnter}
            onMouseLeave={handleSwitcherPanelsMouseLeave}
          >
            {isMobile ? (
              <div
                data-broker-panel
                data-broker-panel-mobile=""
                className={brokerAccountPanelMobileShellClass}
              >
                <MobileBrokerPanelScroll>
                  <DropdownMenuLabel className={brokerAccountPanelLabelClass}>
                    Brokers
                  </DropdownMenuLabel>
                  <Accordion
                    type="single"
                    collapsible
                    value={mobileExpandedBrokerId}
                    onValueChange={setMobileExpandedBrokerId}
                    className="min-w-0"
                  >
                    {brokerGroups.map((broker) => {
                      const isActiveBroker =
                        Boolean(activeBrokerName) && broker.id === activeBrokerName
                      const brokerAccounts = sortAccountsActiveFirst(
                        broker.accounts,
                        activeBrokerName,
                        activeAccountCode
                      )
                      return (
                        <AccordionItem
                          key={broker.id}
                          value={broker.id}
                          className="border-border"
                        >
                          <AccordionTrigger className={brokerAccordionTriggerClass}>
                            <div
                              data-broker-row-inner
                              className={cn(
                                brokerAccountPanelRowInnerClass,
                                "min-w-0 flex-1"
                              )}
                            >
                              <BrokerMark
                                brokerName={broker.id}
                                displayName={broker.name}
                                className="size-6 rounded-md"
                              />
                              <span
                                className={cn(
                                  "truncate font-medium",
                                  isActiveBroker && sidebarTropicalSurfClass
                                )}
                              >
                                {broker.name}
                              </span>
                              <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                                {broker.accounts.length}
                              </span>
                              <ChevronRight
                                className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/accordion-trigger:rotate-90"
                                aria-hidden
                              />
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="pb-0">
                            <div className="flex flex-col">
                              {brokerAccounts.map((account) => (
                                <AccountSelectRow
                                  key={`${account.brokerName}:${account.accountCode}`}
                                  account={account}
                                  brokerGroupLabel={broker.name}
                                  active={isActiveAccount(
                                    account,
                                    activeBrokerName,
                                    activeAccountCode
                                  )}
                                  disabled={isSwitching}
                                  onSelect={(brokerName, accountCode) =>
                                    void handleSelectAccount(brokerName, accountCode)
                                  }
                                />
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      )
                    })}
                  </Accordion>
                  <DropdownMenuSeparator className="-mx-2" />
                  <Link
                    to={PROFILE_BROKER_URL}
                    data-sidebar="menu-button"
                    className={cn(brokerAccountPanelRowClass, "w-full text-left")}
                  >
                    <div
                      data-broker-row-inner
                      className={brokerAccountPanelRowInnerClass}
                    >
                      <div
                        data-broker-mark-frame
                        className={cn(
                          "flex size-6 items-center justify-center",
                          brokerMarkFrameClass
                        )}
                      >
                        <Plus className="size-4 text-muted-foreground" />
                      </div>
                      <span className="font-medium text-muted-foreground">
                        Manage brokers
                      </span>
                    </div>
                  </Link>
                </MobileBrokerPanelScroll>
              </div>
            ) : (
              <>
                <div
                  data-broker-panel
                  className={cn(brokerAccountPanelShellClass, "min-w-56 shrink-0")}
                  onMouseEnter={handleBrokerPanelMouseEnter}
                >
                  <DropdownMenuLabel className={brokerAccountPanelLabelClass}>
                    Brokers
                  </DropdownMenuLabel>
                  {brokerGroups.map((broker) => {
                    const isActiveBroker =
                      Boolean(activeBrokerName) && broker.id === activeBrokerName
                    return (
                      <button
                        key={broker.id}
                        type="button"
                        data-sidebar="menu-button"
                        data-active={displayBrokerId === broker.id ? "true" : undefined}
                        data-pinned={pinnedBrokerId === broker.id ? "true" : undefined}
                        className={cn(brokerAccountPanelRowClass, "text-left")}
                        onMouseEnter={() => handleBrokerRowMouseEnter(broker.id)}
                        onClick={() => pinBroker(broker.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault()
                            pinBroker(broker.id)
                          }
                        }}
                      >
                        <div
                          data-broker-row-inner
                          className={brokerAccountPanelRowInnerClass}
                        >
                          <BrokerMark
                            brokerName={broker.id}
                            displayName={broker.name}
                            className="size-6 rounded-md"
                          />
                          <span
                            className={cn(
                              "truncate font-medium",
                              isActiveBroker && sidebarTropicalSurfClass
                            )}
                          >
                            {broker.name}
                          </span>
                          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                            {broker.accounts.length}
                          </span>
                          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
                        </div>
                      </button>
                    )
                  })}
                  <DropdownMenuSeparator className="-mx-2" />
                  <Link
                    to={PROFILE_BROKER_URL}
                    data-sidebar="menu-button"
                    className={cn(brokerAccountPanelRowClass, "w-full text-left")}
                  >
                    <div
                      data-broker-row-inner
                      className={brokerAccountPanelRowInnerClass}
                    >
                      <div
                        data-broker-mark-frame
                        className={cn(
                          "flex size-6 items-center justify-center",
                          brokerMarkFrameClass
                        )}
                      >
                        <Plus className="size-4 text-muted-foreground" />
                      </div>
                      <span className="font-medium text-muted-foreground">
                        Manage brokers
                      </span>
                    </div>
                  </Link>
                </div>
                {displayBroker ? (
                  <div
                    data-broker-accounts-panel
                    className={cn(
                      brokerAccountPanelShellClass,
                      "min-w-52 shrink-0 self-start"
                    )}
                    onMouseEnter={() => handleBrokerRowMouseEnter(displayBroker.id)}
                  >
                    <DropdownMenuLabel className={brokerAccountPanelLabelClass}>
                      Accounts
                    </DropdownMenuLabel>
                    <AccountsListScroll accountCount={accountsForDisplayBroker.length}>
                      {accountsForDisplayBroker.map((account) => (
                        <AccountSelectRow
                          key={`${account.brokerName}:${account.accountCode}`}
                          account={account}
                          brokerGroupLabel={displayBroker.name}
                          active={isActiveAccount(
                            account,
                            activeBrokerName,
                            activeAccountCode
                          )}
                          disabled={isSwitching}
                          onSelect={(brokerName, accountCode) =>
                            void handleSelectAccount(brokerName, accountCode)
                          }
                        />
                      ))}
                    </AccountsListScroll>
                  </div>
                ) : null}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
